// components/create/RSVPSection.js
import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/RSVPSection.scss';

// Import modals
import PrivacySelector from './PrivacySelector';
import EmojiSetSelector from './EmojiSetSelector';
import GuestListSelector from './GuestListSelector';

// Services
import { getUserGuestLists } from '../../services/guestListService';
import Icon from '../Icon';
import { DateSheet, DateTimeField } from './DateTimeSheet';

export default function RSVPSection({
  privacy,
  setPrivacy,
  rsvpDeadline,
  setRsvpDeadline,
  maxAttendees,
  setMaxAttendees,
  plusOnesAllowed,
  setPlusOnesAllowed,
  hideMaybeRsvp,
  setHideMaybeRsvp,
  enableWaitlist,
  setEnableWaitlist,
  hideLocationBeforeVerification,
  setHideLocationBeforeVerification,
  emojiSet,
  setEmojiSet,
  customEmojis,
  setCustomEmojis,
  guestListIds = [],
  setGuestListIds,
  showInExploreFeed = false,  // NEW: for private events
  setShowInExploreFeed,        // NEW: for private events
  errors = {},
}) {
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showDeadlineSheet, setShowDeadlineSheet] = useState(false);
  const [showEmojiModal, setShowEmojiModal] = useState(false);
  const [showGuestListModal, setShowGuestListModal] = useState(false);
  const [limitSpots, setLimitSpots] = useState(!!maxAttendees);
  const [guestLists, setGuestLists] = useState([]);

  // Load guest lists when privacy is private or when guestListIds change
  const loadSelectedGuestLists = useCallback(async () => {
    try {
      if (guestListIds.length === 0) {
        setGuestLists([]);
        return;
      }
      
      const allLists = await getUserGuestLists();
      const selected = allLists.filter(list => guestListIds.includes(list.id));
      console.log('📋 Selected guest lists in RSVP Section:', selected.length);
      setGuestLists(selected);
    } catch (error) {
      console.error('Error loading guest lists:', error);
    }
  }, [guestListIds]);

  useEffect(() => {
    if (privacy === 'private') {
      loadSelectedGuestLists();
    }
  }, [privacy, guestListIds, loadSelectedGuestLists]);

  const handleGuestListsSelected = (selectedIds) => {
    setGuestListIds(selectedIds);
    setShowGuestListModal(false);
  };

  const handleRemoveGuestList = (listId) => {
    const updatedIds = guestListIds.filter(id => id !== listId);
    setGuestListIds(updatedIds);
  };

  const handleCreateGuestList = () => {
    setShowGuestListModal(false);
    // Navigate to guest lists page
    window.location.href = '/guest-lists';
  };

  const getPrivacyLabel = () => {
    switch (privacy) {
      case 'public':
        return 'Public';
      case 'private':
        return 'Private';
      case 'approval':
        return 'Request Approval';
      default:
        return 'Select privacy';
    }
  };

  const getPrivacyDescription = () => {
    switch (privacy) {
      case 'public':
        return 'Anyone can see and RSVP';
      case 'private':
        return 'Only invited guests can see';
      case 'approval':
        return 'Guests must request to attend';
      default:
        return '';
    }
  };

  return (
    <div className="rsvp-section">
      {/* Privacy Settings */}
      <div className="section-card">
        <label className="section-title">Privacy Settings</label>
        
        <button
          type="button"
          className="select-button"
          onClick={() => setShowPrivacyModal(true)}
        >
          <Icon name={privacy === 'public' ? 'globe-outline' : 'lock-closed-outline'} />
          <div className="select-content">
            <span className="select-text">{getPrivacyLabel()}</span>
            <span className="select-subtext">{getPrivacyDescription()}</span>
          </div>
          <Icon name="chevron-forward" />
        </button>

        {/* NEW: Display in Explore Feed Toggle (Private Events Only) */}
        {privacy === 'private' && (
          <div className="toggle-row">
            <div className="toggle-content">
              <p className="toggle-label">Display invitation in explore feed</p>
              <p className="toggle-description">
                Show this private event in explore for better reach.
              </p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={showInExploreFeed}
                onChange={(e) => setShowInExploreFeed(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        )}

        {/* Hide location toggle for private/approval events */}
        {(privacy === 'private' || privacy === 'approval') && (
          <div className="toggle-row">
            <div className="toggle-content">
              <p className="toggle-label">Hide location until guests RSVP</p>
              <p className="toggle-description">
                {hideLocationBeforeVerification
                  ? 'Only guests who RSVP see the venue, the map and directions'
                  : 'Anyone with the link can see the venue'}
              </p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={hideLocationBeforeVerification}
                onChange={(e) => setHideLocationBeforeVerification(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        )}
      </div>

      {/* RSVP Deadline */}
      <div className="section-card">
        <label className="section-title">RSVP Deadline *</label>
        {/* The last react-datepicker on the form.
            Worth naming: as long as ANY file imported
            react-datepicker/dist/react-datepicker.css, that light theme was in
            the global stylesheet and could reach anything. Removing the import
            here is what actually takes it out of the bundle. */}
        <DateTimeField
          label="RSVP deadline"
          value={rsvpDeadline}
          placeholder="Select deadline"
          invalid={!!errors.rsvpDeadline}
          format={(d) => d.toLocaleDateString('en-US', {
            month: 'short', day: '2-digit', year: 'numeric',
          })}
          onOpen={() => setShowDeadlineSheet(true)}
        />

        {showDeadlineSheet && (
          <DateSheet
            title="RSVP deadline"
            value={rsvpDeadline}
            onChange={setRsvpDeadline}
            onClose={() => setShowDeadlineSheet(false)}
            minDate={new Date()}
          />
        )}
        {errors.rsvpDeadline && <p className="error-text">{errors.rsvpDeadline}</p>}
      </div>

      {/* Attendee Limits */}
      <div className="section-card">
        <label className="section-title">Attendee Limits</label>
        
        <div className="toggle-row">
          <p className="toggle-label">Limit number of spots</p>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={limitSpots}
              onChange={(e) => {
                setLimitSpots(e.target.checked);
                if (!e.target.checked) setMaxAttendees(null);
              }}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        {limitSpots && (
          <>
            <input
              type="number"
              className="text-input"
              placeholder="Maximum attendees"
              value={maxAttendees || ''}
              onChange={(e) => setMaxAttendees(parseInt(e.target.value) || null)}
              min="1"
            />

            <div className="toggle-row">
              <div className="toggle-content">
                <p className="toggle-label">Enable waiting list</p>
                <p className="toggle-description">
                  Auto-add guests when spots open
                </p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={enableWaitlist}
                  onChange={(e) => setEnableWaitlist(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </>
        )}

        {/* Plus-ones counter */}
        <div className="counter-row">
          <p className="counter-label">Plus-ones allowed</p>
          <div className="counter">
            <button
              type="button"
              className="counter-button"
              onClick={() => setPlusOnesAllowed(Math.max(0, plusOnesAllowed - 1))}
            >
              <Icon name="remove" />
            </button>
            <span className="counter-value">{plusOnesAllowed}</span>
            <button
              type="button"
              className="counter-button"
              onClick={() => setPlusOnesAllowed(Math.min(9, plusOnesAllowed + 1))}
            >
              <Icon name="add" />
            </button>
          </div>
        </div>
      </div>

      {/* RSVP Options */}
      <div className="section-card">
        <label className="section-title">RSVP Options</label>
        
        <div className="toggle-row">
          <p className="toggle-label">Hide "Maybe" option</p>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={hideMaybeRsvp}
              onChange={(e) => setHideMaybeRsvp(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <label className="field-label">RSVP Button Style</label>
        <button
          type="button"
          className="select-button"
          onClick={() => setShowEmojiModal(true)}
        >
          <span className="emoji-preview">
            {emojiSet === 'custom'
              ? `${customEmojis.going} ${customEmojis.maybe} ${customEmojis.notGoing}`
              : 'Default style'}
          </span>
          <Icon name="chevron-forward" />
        </button>
      </div>

      {/* Guest Lists (Private events only) */}
      {privacy === 'private' && (
        <div className="section-card">
          <div className="section-header">
            <label className="section-title">Guest Lists</label>
            <button
              type="button"
              className="manage-button"
              onClick={() => window.location.href = '/guest-lists'}
            >
              <span>Manage</span>
              <Icon name="chevron-forward" />
            </button>
          </div>
          <p className="section-description">
            Select guest lists to invite automatically
          </p>

          {guestLists.length === 0 ? (
            <div className="empty-guest-lists">
              <Icon name="people-outline" />
              <p className="empty-text">No guest lists selected</p>
              <p className="empty-subtext">
                Add guest lists to automatically invite contacts
              </p>
              <button
                type="button"
                className="add-button"
                onClick={() => setShowGuestListModal(true)}
              >
                <Icon name="add-circle" />
                <span>Select Guest Lists</span>
              </button>
            </div>
          ) : (
            <>
              <div className="guest-lists-container">
                {guestLists.map((list) => (
                  <div key={list.id} className="guest-list-item">
                    <div className="guest-list-left">
                      <div className="guest-list-icon">
                        <Icon name="people" />
                      </div>
                      <div className="guest-list-info">
                        <p className="guest-list-name">{list.name}</p>
                        <p className="guest-list-count">
                          {list.contact_count || 0} {(list.contact_count || 0) === 1 ? 'contact' : 'contacts'}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="remove-button"
                      onClick={() => handleRemoveGuestList(list.id)}
                    >
                      <Icon name="close-circle" />
                    </button>
                  </div>
                ))}
              </div>
              
              <button
                type="button"
                className="add-button secondary"
                onClick={() => setShowGuestListModal(true)}
              >
                <Icon name="add-circle" />
                <span>Add More Guest Lists</span>
              </button>
            </>
          )}
        </div>
      )}

      {/* Modals */}
      {showPrivacyModal && (
        <PrivacySelector
          visible={showPrivacyModal}
          onClose={() => setShowPrivacyModal(false)}
          selectedPrivacy={privacy}
          onSelect={(value) => {
            setPrivacy(value);
            setShowPrivacyModal(false);
            // Reset show in explore feed when changing from private
            if (value !== 'private') {
              setShowInExploreFeed(false);
            }
          }}
        />
      )}

      {showEmojiModal && (
        <EmojiSetSelector
          visible={showEmojiModal}
          onClose={() => setShowEmojiModal(false)}
          selectedSet={emojiSet}
          customEmojis={customEmojis}
          onSelect={(set, emojis) => {
            setEmojiSet(set);
            if (emojis) setCustomEmojis(emojis);
            setShowEmojiModal(false);
          }}
        />
      )}

      {showGuestListModal && (
        <GuestListSelector
          visible={showGuestListModal}
          onClose={() => setShowGuestListModal(false)}
          selectedGuestListIds={guestListIds}
          onGuestListsSelected={handleGuestListsSelected}
          onCreateGuestList={handleCreateGuestList}
        />
      )}
    </div>
  );
}